// Simple Web Audio API manager with mute persistence
export class AudioManager {
  constructor() {
    this.audioContext = null;
    this.muted = false;
    this.initialized = false;
    
    // Try to load mute state from localStorage
    try {
      const stored = localStorage.getItem('gemgrid_mute');
      if (stored !== null) {
        this.muted = stored === 'true';
      }
    } catch (e) {
      console.warn('Failed to read localStorage mute state:', e);
    }
  }
  
  init() {
    if (this.initialized) return;
    
    // Create AudioContext on user gesture (call this from a click handler)
    // For now we'll create it suspended and resume later.
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.audioContext.suspend(); // Start suspended until user interaction
      this.initialized = true;
    } catch (e) {
      console.warn('Web Audio API not supported:', e);
    }
  }
  
  // Resume audio context (must be called from a user gesture)
  resume() {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }
  }
  
  // Toggle mute state and persist
  toggleMute() {
    this.muted = !this.muted;
    try {
      localStorage.setItem('gemgrid_mute', this.muted.toString());
    } catch (e) {
      console.warn('Failed to write mute state to localStorage:', e);
    }
    return this.muted;
  }
  
  // Set mute state directly
  setMute(muted) {
    this.muted = muted;
    try {
      localStorage.setItem('gemgrid_mute', this.muted.toString());
    } catch (e) {
      console.warn('Failed to write mute state to localStorage:', e);
    }
  }
  
  // Play a simple beep sound (placeholder)
  playBeep(frequency = 440, duration = 0.1) {
    if (this.muted || !this.audioContext) return;
    
    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);
    
    oscillator.frequency.setValueAtTime(frequency, this.audioContext.currentTime);
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.1, this.audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + duration);
    
    oscillator.start();
    oscillator.stop(this.audioContext.currentTime + duration);
  }
  
  // Play a sound effect for merge
  playMerge(tier) {
    // Higher tier, higher pitch
    const freq = tier === 1 ? 523.25 : tier === 2 ? 659.25 : 783.99; // C5, E5, G5
    this.playBeep(freq, 0.15);
  }
  
  // Play purchase sound
  playPurchase() {
    this.playBeep(349.23, 0.1); // F4
  }
  
  // Play wave start sound
  playWaveStart() {
    this.playBeep(587.33, 0.2); // D5
  }
  
  // Play game over sound
  playGameOver() {
    this.playBeep(220, 0.5); // A3
  }
  
  // Play victory sound
  playVictory() {
    // Play a small arpeggio
    const notes = [523.25, 659.25, 783.99]; // C5, E5, G5
    notes.forEach((freq, i) => {
      setTimeout(() => this.playBeep(freq, 0.15), i * 100);
    });
  }
  
  // Play click sound (for UI)
  playClick() {
    this.playBeep(784, 0.05); // G5
  }
}